import { apiPath } from './client'

/**
 * 把后端渲染好的 HTML 里的图片地址改写到本部署的 API 基址上。
 *
 * ## 为什么必须改写
 *
 * 后端渲染出来的图片地址是**根相对路径**：
 *
 *     <img src="/api/v1/media/vLPUB9aUMui39KAi8l3R_hxoNJ5Hg__e" …>
 *
 * 这是后端刻意的取舍，不是遗漏（`internal/media/public.go` 的
 * `PublicPathPrefix`、`internal/api/render.go` 的 `APIPrefix`，两个都是常量）：
 * 已发布的修订是**不可变**的，`rendered_html` 是发布那一刻就封存的缓存。把域名
 * 烤进去意味着换一次域名就要跑一遍 `cmd/rerender` 重写所有历史修订，所以后端
 * 只写相对路径，由客户端决定它挂在哪个源上。
 *
 * 而浏览器解析根相对路径用的是**当前页面的源**，不是 API 的源。前后端分离部署
 * （`VITE_API_BASE_URL` 填跨源绝对地址）下，正文里的图片于是全部指向前端域名：
 *
 *     https://www.example.com/api/v1/media/{key}      ← 前端源，这里没有这张图
 *     https://api.example.com/api/v1/media/{key}      ← 图片实际在这里
 *
 * ## 它的症状为什么难认
 *
 * 前端源上根本没有这条路由，`<img>` 拿到的不是图片字节；而前端是 Cloudflare
 * Workers 静态站、`not_found_handling` 配的是 `single-page-application`
 * （见 wrangler.jsonc），未命中的路径会回落到 index.html —— 也就是说浏览器
 * 多半是拿一份 HTML 去当图片解码，连一条 404 都不会出现在控制台里。
 *
 * 更容易带偏方向的是：封面与图集**是好的**。它们走 `mediaUrl()`（经过
 * `apiPath`），只有塞进 `dangerouslySetInnerHTML` 的这一份 HTML 绕开了基址，
 * 于是看起来像「只有正文里的图坏了」这种没有道理的现象。
 *
 * 这正是 `apiPath` 注释里警告过的那类故障：「通常只表现为图片坏了、接口都正常」。
 *
 * ## 用在哪
 *
 * 所有把后端 HTML 交给 `dangerouslySetInnerHTML` 的地方，目前两处：读者的
 * 详情页（`/posts/{slug}` 的 `rendered_html`，图片是公开出图路径）与管理端的
 * 草稿预览（`/admin/posts/{id}/draft/preview`，图片是 `/api/v1/admin/
 * media-assets/{id}/preview`）。两者前缀相同，所以这里不区分。
 */

/**
 * 后端写进 HTML 的 API 前缀。
 *
 * 它是契约里的常量而不是配置项 —— 后端那两处也是硬编码的 `const`，所以这里
 * 照抄一份是安全的；真要变，那是一次需要 rerender 的破坏性变更，两边都得改。
 */
const RENDERED_API_PREFIX = '/api/v1'

/**
 * 本部署到底需不需要改写。
 *
 * 同站部署（`VITE_API_BASE_URL` 留空，基址就是 `/api/v1`）下改写是恒等变换，
 * 连解析一遍 HTML 都不必。开发期的 vite 代理也走这一条。
 */
const NEEDS_REBASE = apiPath('/') !== `${RENDERED_API_PREFIX}/`

export function rebaseRenderedHtml(html: string): string {
  if (!NEEDS_REBASE || !html.includes(`${RENDERED_API_PREFIX}/`)) return html

  /*
    用 DOMParser 而不是字符串替换：要改的是 `src` **属性**，而 `/api/v1/` 这几个
    字符完全可能出现在正文的可见文字里（这个站的正文就经常在讲接口）。
    字符串替换会把读者写的一段话悄悄改掉，而且改的是他看得见的内容。

    `parseFromString` 产出的是没有浏览上下文的惰性文档：它不会为这些 `<img>`
    发起任何请求，因此改写发生在浏览器尝试加载那个错地址**之前** ——
    换成挂载后再用 useEffect 修 `src`，就会先白白打一次错请求并闪一下坏图。
  */
  const doc = new DOMParser().parseFromString(html, 'text/html')
  let changed = false

  for (const image of doc.querySelectorAll('img')) {
    /*
      取属性原文，不能用 `image.src`：后者返回的是浏览器解析过的绝对地址
      （也就是解析到前端源上的那个错地址），拿它判前缀永远不成立，
      于是这个函数会安静地什么都不做。
    */
    const src = image.getAttribute('src')
    if (!src?.startsWith(`${RENDERED_API_PREFIX}/`)) continue
    image.setAttribute('src', apiPath(src.slice(RENDERED_API_PREFIX.length)))
    changed = true
  }

  // 没改动就原样返回，不让这段 HTML 白白过一遍解析器的序列化。
  return changed ? doc.body.innerHTML : html
}
